import React, { useContext, useState, useEffect } from 'react';
import { Grid, useMediaQuery, Theme } from '@mui/material';
import { EngagementViewContext } from './EngagementViewContext';
import { EngagementContent } from './EngagementContent';
import SurveyBlock from './SurveyBlock';
import EmailModal from './EmailModal';
import { PreviewBanner } from 'components/admin/engagement/preview/PreviewBanner';
import { useAppSelector } from 'hooks';
import { useNavigate, useLocation } from 'react-router';
import { RouteState } from './types';
import WidgetBlock from './widgets/WidgetBlock';
import { Else, If, Then } from 'react-if';
import { PhasesWidget } from './widgets/PhasesWidget';
import { PhasesWidgetMobile } from './widgets/PhasesWidget/PhasesWidgetMobile/PhasesWidgetMobile';
import { EngagementBanner } from './EngagementBanner';
import { analyticsService } from 'services/penguinAnalytics';
import { SubmissionStatus } from 'constants/engagementStatus';

export const EngagementView = () => {
    const { state } = useLocation() as RouteState;
    const [isEmailModalOpen, setEmailModalOpen] = useState(state ? state.open : false);
    const [defaultPanel, setDefaultPanel] = useState(state ? 'thank you' : 'email');
    const isLoggedIn = useAppSelector((state) => state.user.authentication.authenticated);
    const roles = useAppSelector((state) => state.user.roles);
    const isPreview = isLoggedIn;
    const { savedEngagement } = useContext(EngagementViewContext);
    const surveyId = savedEngagement.surveys[0]?.id || '';
    const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    //Clear state on window refresh
    window.history.replaceState({}, document.title);

    const isMediumScreen: boolean = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

    // Track page view with engagement_id and user_type for analytics
    // Uses roles.length (not isLoggedIn) to correctly classify authenticated public stakeholders as 'public'
    useEffect(() => {
        if (savedEngagement?.id) {
            const userType = roles.length > 0 ? 'admin' : 'public';
            const status = SubmissionStatus[savedEngagement.submission_status] || 'Unknown';
            analyticsService.page(savedEngagement.name || 'Engagement Page', String(savedEngagement.id), userType, savedEngagement.name, status);
        }
    }, [savedEngagement?.id, savedEngagement?.name, savedEngagement?.submission_status, roles]);

    const handleStartSurvey = () => {
        if (!isPreview) {
            const userType = roles.length > 0 ? 'admin' : 'public';
            analyticsService.track({
                action: 'cta_click',
                engagement_id: String(savedEngagement.id),
                engagement_name: savedEngagement.name,
                text: 'Share Your Thoughts',
                user_type: userType,
            });
            setDefaultPanel('email');
            setEmailModalOpen(true);
            try {
                window.snowplow('trackPageView', 'Verify Email Modal');
            } catch (error) {
                console.log(error);
            }
            return;
        }

        navigate(`/surveys/${surveyId}/submit`);
    };

    const handleClose = () => {
        setEmailModalOpen(false);
        setDefaultPanel('email');
    };

    return (
        <>
            <EmailModal defaultPanel={defaultPanel} open={isEmailModalOpen} handleClose={() => handleClose()} />
            <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
                <Grid item xs={12}>
                    <PreviewBanner />
                </Grid>
                <Grid item xs={12}>
                    <EngagementBanner />
                </Grid>
                <Grid
                    container
                    item
                    xs={12}
                    direction="row"
                    justifyContent={'flex-start'}
                    alignItems="flex-start"
                    m={{ md: '1em', xs: '1em' }}
                    rowSpacing={2}
                    columnSpacing={1}
                >
                    <Grid item xs={12}>
                        <If condition={isSmallScreen}>
                            <Then>
                                <PhasesWidgetMobile />
                            </Then>
                            <Else>
                                <PhasesWidget />
                            </Else>
                        </If>
                    </Grid>
                    <If condition={isMediumScreen}>
                        <Then>
                            <Grid
                                container
                                item
                                xs={12}
                                md={8}
                                direction="row"
                                justifyContent={'flex-start'}
                                alignItems="flex-start"
                                rowSpacing={2}
                                columnSpacing={1}
                            >
                                <Grid data-testid={'engagement-content'} item xs={12}>
                                    <EngagementContent />
                                </Grid>
                                <Grid item xs={12}>
                                    <SurveyBlock startSurvey={handleStartSurvey} />
                                </Grid>
                            </Grid>
                            <Grid item data-testid={'widget-block'} xs={12} md={4}>
                                <WidgetBlock />
                            </Grid>
                        </Then>
                        <Else>
                            <Grid data-testid={'engagement-content'} item xs={12}>
                                <EngagementContent />
                            </Grid>
                            <Grid item data-testid={'widget-block'} xs={12}>
                                <WidgetBlock />
                            </Grid>
                            <Grid item xs={12}>
                                <SurveyBlock startSurvey={handleStartSurvey} />
                            </Grid>
                        </Else>
                    </If>
                </Grid>
            </Grid>
        </>
    );
};

export default EngagementView;
